import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  CalibrationHistoryEntry,
  CalibrationSettings,
  Skill,
  SettingsFormData,
} from 'talent_matching/types';

type InitialSkills = {
  top_skills: Skill[];
  scorecard_skills: Skill[];
  pipeline_skills: Skill[];
};

type Params = {
  isOpen: boolean;
  isHistoryTab: boolean;
  selectedHistory?: CalibrationHistoryEntry;
  settingsExist: boolean;
  settings?: CalibrationSettings | null;
  setValue: ReturnType<typeof useFormContext>['setValue'];
  getValues: ReturnType<typeof useFormContext>['getValues'];
  reset: (values: Partial<SettingsFormData>) => void;
  buildFormValuesFromCalibration: (
    calibration: Partial<CalibrationSettings>
  ) => Partial<SettingsFormData>;
  hasJobDescription: boolean;
  hasScorecardAttributes: boolean;
  initialSkills?: InitialSkills;
  fetchedSuggestedPrompt?: string | null;
  setSuggestedPrompt: (value: string | null) => void;
  setSuggestedPromptError: (value: string | null) => void;
  applyingHistoryRef: React.MutableRefObject<boolean>;
};

export const useCalibrationFormStateSync = ({
  isOpen,
  isHistoryTab,
  selectedHistory,
  settingsExist,
  settings,
  setValue,
  getValues,
  reset,
  buildFormValuesFromCalibration,
  hasJobDescription,
  hasScorecardAttributes,
  initialSkills,
  fetchedSuggestedPrompt,
  setSuggestedPrompt,
  setSuggestedPromptError,
  applyingHistoryRef,
}: Params) => {
  const settingsInitializedRef = useRef(false);
  const lastHistoryIdRef = useRef<number | null>(null);
  const lastTabRef = useRef<boolean>(isHistoryTab);
  const workingFormValuesRef = useRef<Partial<SettingsFormData> | null>(null);

  const applyFetchedFormState = useCallback(() => {
    if (settingsExist && settings) {
      const formValues = buildFormValuesFromCalibration(settings);
      reset(formValues);

      setValue('extracted_skills', settings.extracted_skills, {
        shouldDirty: false,
      });
      if (settings.userPrompt) {
        setValue('userPrompt', settings.userPrompt, { shouldDirty: false });
        setValue('userPromptMode', 'view', { shouldDirty: false });
      }
    }

    setValue('include_job_description', hasJobDescription);
    setValue('include_scorecard_attributes', hasScorecardAttributes);

    if (!settings?.userPrompt) {
      setValue('userPromptMode', 'edit');
    }

    if (initialSkills) {
      setValue('jobPostSkills', initialSkills.top_skills);
      setValue('scorecardSkills', initialSkills.scorecard_skills);
      setValue(
        'pipelineSkills',
        initialSkills.pipeline_skills.map((skill: Skill) => ({
          ...skill,
          isPipelineSkill: true,
        }))
      );
    }
  }, [
    settingsExist,
    settings,
    buildFormValuesFromCalibration,
    reset,
    setValue,
    hasJobDescription,
    hasScorecardAttributes,
    initialSkills,
  ]);

  useEffect(() => {
    if (!isOpen) {
      settingsInitializedRef.current = false;
      lastHistoryIdRef.current = null;
      lastTabRef.current = isHistoryTab;
      applyingHistoryRef.current = false;
      workingFormValuesRef.current = null;
      return;
    }

    if (settingsExist && !settings) {
      return;
    }

    if (settingsExist && settings && settingsInitializedRef.current) {
      return;
    }

    // Defer initialization slightly to ensure form fields are fully registered
    setTimeout(() => {
      applyFetchedFormState();
      if (settingsExist && settings) {
        settingsInitializedRef.current = true;
      }
    }, 0);
  }, [
    isOpen,
    settingsExist,
    settings,
    applyFetchedFormState,
    isHistoryTab,
    applyingHistoryRef,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const tabChanged = lastTabRef.current !== isHistoryTab;
    lastTabRef.current = isHistoryTab;

    // Only snapshot once the fetched calibration has populated the form.
    // Capturing before the fetch resolves would stash empty defaults and
    // clobber the real calibration when the user returns to Current.
    if (tabChanged && isHistoryTab) {
      const fetchedDataReady =
        (!settingsExist || !!settings) && !!initialSkills;
      workingFormValuesRef.current = fetchedDataReady
        ? (getValues() as SettingsFormData)
        : null;
    }

    if (isHistoryTab && selectedHistory) {
      const historyId = selectedHistory.id;
      const historyChanged = lastHistoryIdRef.current !== historyId;

      if (tabChanged || historyChanged) {
        reset(buildFormValuesFromCalibration(selectedHistory));
        lastHistoryIdRef.current = historyId;
      }
    }

    if (!isHistoryTab && tabChanged) {
      if (applyingHistoryRef.current) {
        applyingHistoryRef.current = false;
        workingFormValuesRef.current = null;
        return;
      }

      if (workingFormValuesRef.current) {
        reset(workingFormValuesRef.current);
        workingFormValuesRef.current = null;
      } else {
        applyFetchedFormState();
      }
      lastHistoryIdRef.current = null;
    }
  }, [
    isOpen,
    isHistoryTab,
    selectedHistory,
    settingsExist,
    settings,
    initialSkills,
    getValues,
    reset,
    buildFormValuesFromCalibration,
    applyFetchedFormState,
    applyingHistoryRef,
  ]);

  useEffect(() => {
    if (fetchedSuggestedPrompt) {
      setSuggestedPrompt(fetchedSuggestedPrompt);
      setSuggestedPromptError(null);
    }
  }, [fetchedSuggestedPrompt, setSuggestedPrompt, setSuggestedPromptError]);
};
